import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { socialPostScheduleLine, type SocialPostRendition } from './social-post-preview-card';

export const FACEBOOK_CAPTION_LIMIT = 250;
export const FACEBOOK_REEL_CAPTION_LIMIT = 60;
export const FACEBOOK_GRID_TILES = 4;
export const FACEBOOK_TALL_FRAME_RATIO = 9 / 16;
const TALL_FRAME_TOLERANCE = 0.03;

export type FacebookLook = 'feed' | 'reel' | 'story';

export type FacebookMediaGrid =
  | { kind: 'none' }
  | { kind: 'single'; tile: SocialPostPreviewMedia }
  | { kind: 'two' | 'three'; tiles: SocialPostPreviewMedia[] }
  | { kind: 'four'; tiles: SocialPostPreviewMedia[]; hidden: number };

export type FacebookCaptionSegment = { text: string; highlight: boolean };

const HASHTAG = String.raw`#[\p{L}\p{M}\p{N}_]+`;
const WEB_ADDRESS = String.raw`(?:https?:\/\/|www\.)\S+`;
const HASHTAGS_AND_URLS = new RegExp(`${HASHTAG}|${WEB_ADDRESS}`, 'gu');
const URLS_ONLY = new RegExp(WEB_ADDRESS, 'gu');
const URL_TRAILING_PUNCTUATION = /[.,!?)…]+$/u;

export function isFacebookPost(card: Pick<SocialPostPreviewCardMetadata, 'network'>): boolean {
  return card.network === 'facebook';
}

export function facebookLook(card: Pick<SocialPostPreviewCardMetadata, 'postType' | 'media'>): FacebookLook {
  if (card.media.length === 0) {
    return 'feed';
  }
  if (card.postType === 'reel') {
    return 'reel';
  }
  if (card.postType === 'story') {
    return 'story';
  }
  return 'feed';
}

export function facebookPageName(card: Pick<SocialPostPreviewCardMetadata, 'accountName' | 'networkLabel'>): string {
  return card.accountName ?? card.networkLabel;
}

export function facebookInitial(name: string): string {
  return Array.from(name.trim())[0]?.toUpperCase() ?? '';
}

export function facebookTimingLine(card: SocialPostPreviewCardMetadata, locale: string | undefined): string {
  const timing = socialPostScheduleLine(card, locale);
  return card.labels.postType ? `${timing} · ${card.labels.postType}` : timing;
}

export function facebookCaptionText(caption: string, expanded: boolean, limit = FACEBOOK_CAPTION_LIMIT): { text: string; truncated: boolean } {
  const codePoints = Array.from(caption);
  if (expanded || codePoints.length <= limit) {
    return { text: caption, truncated: false };
  }
  return { text: `${codePoints.slice(0, limit).join('')}...`, truncated: true };
}

export function facebookCaptionSegments(text: string, { hashtags = true }: { hashtags?: boolean } = {}): FacebookCaptionSegment[] {
  const segments: FacebookCaptionSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(hashtags ? HASHTAGS_AND_URLS : URLS_ONLY)) {
    const start = match.index ?? 0;
    if (start < cursor) {
      continue;
    }
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), highlight: false });
    }
    const marked = match[0].startsWith('#') ? match[0] : match[0].replace(URL_TRAILING_PUNCTUATION, '');
    segments.push({ text: marked, highlight: true });
    cursor = start + marked.length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlight: false });
  }
  return segments;
}

export function facebookMediaTotal(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount'>): number {
  return Math.max(card.mediaCount, card.media.length);
}

export function facebookMediaGrid(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount'>): FacebookMediaGrid {
  const { media } = card;
  if (media.length === 0) {
    return { kind: 'none' };
  }
  if (media.length === 1) {
    return { kind: 'single', tile: media[0] };
  }
  if (media.length === 2) {
    return { kind: 'two', tiles: [...media] };
  }
  if (media.length === 3) {
    return { kind: 'three', tiles: [...media] };
  }
  const tiles = media.slice(0, FACEBOOK_GRID_TILES);
  return { kind: 'four', tiles, hidden: facebookMediaTotal(card) - FACEBOOK_GRID_TILES };
}

export function facebookLinkCardUrl(link: string | undefined): string | undefined {
  if (!link) {
    return undefined;
  }
  try {
    const { protocol, hostname } = new URL(link);
    return protocol === 'https:' && hostname ? link : undefined;
  } catch {
    return undefined;
  }
}

export function facebookLinkDomain(link: string): string | undefined {
  try {
    return new URL(link).hostname.replace(/^www\./i, '') || undefined;
  } catch {
    return undefined;
  }
}

export function facebookFillsTallFrame(width: number, height: number): boolean {
  return width > 0 && height > 0 && Math.abs(width / height - FACEBOOK_TALL_FRAME_RATIO) <= TALL_FRAME_TOLERANCE;
}

export const FACEBOOK_RENDITIONS: Record<'single' | 'tall' | 'quarter' | 'reel' | 'story', SocialPostRendition> = {
  single: { mode: 'fill', width: 660, height: 668 },
  tall: { mode: 'fill', width: 328, height: 668 },
  quarter: { mode: 'fill', width: 328, height: 332 },
  reel: { mode: 'fit', width: 564, height: 1002 },
  story: { mode: 'fit', width: 600, height: 1066 },
};

export function facebookGridRendition(kind: 'two' | 'three' | 'four', index: number): SocialPostRendition {
  return kind === 'two' || (kind === 'three' && index === 0) ? FACEBOOK_RENDITIONS.tall : FACEBOOK_RENDITIONS.quarter;
}

export function facebookShowsNotShown(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount' | 'labels'>): boolean {
  return facebookMediaGrid(card).kind !== 'four' && card.mediaCount > card.media.length && !!card.labels.mediaNotShown;
}

export function facebookEndsFullBleed(card: SocialPostPreviewCardMetadata): boolean {
  if (facebookLook(card) !== 'feed' || (card.expiresWithinDay && card.labels.expires)) {
    return false;
  }
  if (card.media.length > 0) {
    return !facebookShowsNotShown(card);
  }
  return !!facebookLinkCardUrl(card.link);
}
